import * as XLSX from 'xlsx';
import { DEFAULT_REST_TIME_SECONDS } from '../enums/RestTimeDefaults';

// Field definitions with keywords for auto-detection (Hebrew and English)
export interface FieldDefinition {
    label: string;
    keywords: string[];
}

export type FieldKey =
    | 'workoutId'
    | 'exerciseName'
    | 'sets'
    | 'reps'
    | 'restTime'
    | 'minReps'
    | 'maxReps'
    | 'duration'
    | 'tempo'
    | 'typeSet'
    | 'manipulation'
    | 'comment';

export const FIELD_DEFINITIONS: Record<FieldKey, FieldDefinition> = {
    workoutId: {
        label: 'מספר אימון',
        keywords: ['מספר אימון', 'אימון', 'workout', 'workout id', 'day', 'יום']
    },
    exerciseName: {
        label: 'שם תרגיל',
        keywords: ['שם תרגיל', 'תרגיל', 'exercise', 'exercise name', 'name']
    },
    sets: {
        label: 'מספר סטים',
        keywords: ['מספר סטים', 'סטים', 'sets', 'number of sets']
    },
    reps: {
        label: 'מספר חזרות',
        keywords: ['מספר חזרות', 'חזרות', 'reps', 'repetitions', 'recommended reps']
    },
    restTime: {
        label: 'זמן מנוחה',
        keywords: ['זמן מנוחה', 'מנוחה', 'rest', 'rest time', 'שניות']
    },
    // Optional fields
    minReps: {
        label: 'מינימום חזרות',
        keywords: ['מינימום חזרות', 'min reps', 'minimum reps']
    },
    maxReps: {
        label: 'מקסימום חזרות',
        keywords: ['מקסימום חזרות', 'max reps', 'maximum reps']
    },
    duration: {
        label: 'משך זמן',
        keywords: ['משך זמן', 'duration', 'time']
    },
    tempo: {
        label: 'טמפו',
        keywords: ['טמפו', 'tempo']
    },
    typeSet: {
        label: 'סוג סט',
        keywords: ['סוג סט', 'type set', 'set type']
    },
    manipulation: {
        label: 'מניפולציה',
        keywords: ['מניפולציה', 'manipulation', 'progression']
    },
    comment: {
        label: 'הערה',
        keywords: ['הערה', 'הערות', 'comment', 'comments', 'note', 'notes']
    }
};

export interface FieldOption {
    value: string;
    label: string;
}

export const FIELD_OPTIONS: FieldOption[] = [
    { value: 'unknown', label: 'לא ידוע' },
    { value: 'ignore', label: 'התעלם' },
    { value: 'workoutId', label: 'מספר אימון' },
    { value: 'exerciseName', label: 'שם תרגיל' },
    { value: 'sets', label: 'מספר סטים' },
    { value: 'reps', label: 'מספר חזרות' },
    { value: 'restTime', label: 'זמן מנוחה' },
    { value: 'minReps', label: 'מינימום חזרות' },
    { value: 'maxReps', label: 'מקסימום חזרות' },
    { value: 'duration', label: 'משך זמן' },
    { value: 'tempo', label: 'טמפו' },
    { value: 'typeSet', label: 'סוג סט' },
    { value: 'manipulation', label: 'מניפולציה' },
    { value: 'comment', label: 'הערה' }
];

// Valid enum values for validation
export const VALID_TEMPO_VALUES: string[] = [
    'T1_0_1_0', 'T1_0_0_2', 'T1_0_1_3', 'T1_1_1_1', 'T1_2_1_1',
    'T2_0_1_0', 'T2_0_1_2', 'T2_0_1_3', 'T2_1_1_2', 'T2_1_1_3'
];

export const VALID_TYPE_SET_VALUES: string[] = [
    'REGULAR_SET', 'MAX_REPS', 'SUPER_SET', 'DROP_SET',
    'CLUSTER_SET', 'REST_PAUSE', 'MYO_REPS', 'RM_SET'
];

export const VALID_MANIPULATION_VALUES: string[] = ['A1', 'WP', 'RP_NEW'];

// A detected field is a real field key, or one of the sentinel markers.
type DetectedField = FieldKey | 'unknown' | 'ignore';

// Detect field type from header text
const detectFieldFromHeader = (header: unknown): DetectedField => {
    if (!header || typeof header !== 'string') return 'unknown';

    const normalizedHeader = header.toLowerCase().trim();

    for (const [fieldKey, fieldDef] of Object.entries(FIELD_DEFINITIONS)) {
        for (const keyword of fieldDef.keywords) {
            if (normalizedHeader.includes(keyword.toLowerCase())) {
                return fieldKey as FieldKey;
            }
        }
    }

    return 'unknown';
};

// Detect field type from data pattern (fallback)
const detectFieldFromData = (values: unknown[]): DetectedField => {
    if (!values || values.length === 0) return 'unknown';

    const sampleValues = values.slice(0, 10).filter(v => v && String(v).trim());
    if (sampleValues.length === 0) return 'unknown';

    // Check for workout ID pattern (A, B, C or 1, 2, 3)
    const workoutIdPattern = /^[A-Za-z]$|^[1-9]$/;
    const workoutIdMatches = sampleValues.filter(v => workoutIdPattern.test(String(v).trim())).length;
    if (workoutIdMatches >= sampleValues.length * 0.7) return 'workoutId';

    // Check for small numbers (sets: 1-10)
    const setsPattern = sampleValues.filter(v => {
        const num = parseInt(String(v));
        return !isNaN(num) && num >= 1 && num <= 10;
    }).length;
    if (setsPattern >= sampleValues.length * 0.7) return 'sets';

    // Check for medium numbers (reps: 1-50)
    const repsPattern = sampleValues.filter(v => {
        const num = parseInt(String(v));
        return !isNaN(num) && num >= 1 && num <= 50;
    }).length;
    if (repsPattern >= sampleValues.length * 0.7) return 'reps';

    // Check for large numbers (rest time: 30-300)
    const restTimePattern = sampleValues.filter(v => {
        const num = parseInt(String(v));
        return !isNaN(num) && num >= 30 && num <= 300;
    }).length;
    if (restTimePattern >= sampleValues.length * 0.7) return 'restTime';

    // Check for tempo values
    const tempoMatches = sampleValues.filter(v =>
        VALID_TEMPO_VALUES.includes(String(v).toUpperCase().trim())
    ).length;
    if (tempoMatches >= sampleValues.length * 0.5) return 'tempo';

    // Check for type set values
    const typeSetMatches = sampleValues.filter(v =>
        VALID_TYPE_SET_VALUES.includes(String(v).toUpperCase().trim().replace(/ /g, '_'))
    ).length;
    if (typeSetMatches >= sampleValues.length * 0.5) return 'typeSet';

    // Check for manipulation values
    const manipulationMatches = sampleValues.filter(v =>
        VALID_MANIPULATION_VALUES.includes(String(v).toUpperCase().trim())
    ).length;
    if (manipulationMatches >= sampleValues.length * 0.5) return 'manipulation';

    // Default to exercise name if it's mostly text (Hebrew or English)
    const textPattern = sampleValues.filter(v => {
        const str = String(v).trim();
        return str.length > 2 && isNaN(parseInt(str));
    }).length;
    if (textPattern >= sampleValues.length * 0.7) return 'exerciseName';

    return 'unknown';
};

export interface ParsedTable {
    headers: string[];
    rows: string[][];
}

// Parse CSV content
const parseCSV = (content: string): ParsedTable => {
    const lines = content.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    // Detect delimiter (comma, tab, or semicolon)
    const firstLine = lines[0];
    let delimiter = ',';
    if (firstLine.includes('\t') && !firstLine.includes(',')) {
        delimiter = '\t';
    } else if (firstLine.includes(';') && !firstLine.includes(',')) {
        delimiter = ';';
    }

    const parseRow = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === delimiter && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current.trim());

        return result;
    };

    const allRows = lines.map(parseRow);

    // Check if first row looks like headers
    const firstRow = allRows[0];
    const headerMatchCount = firstRow.filter(cell => {
        const normalized = String(cell).toLowerCase().trim();
        if (!normalized) return false;
        return Object.values(FIELD_DEFINITIONS).some(def =>
            def.keywords.some(k => {
                const keyword = k.toLowerCase();
                return normalized === keyword ||
                       normalized === keyword + ':' ||
                       normalized.split(/\s+/).includes(keyword);
            })
        );
    }).length;

    const hasHeaders = headerMatchCount >= 2;

    if (hasHeaders) {
        return {
            headers: firstRow,
            rows: allRows.slice(1)
        };
    }

    return {
        headers: [],
        rows: allRows
    };
};

// Parse Excel file
const parseExcel = (buffer: ArrayBuffer): ParsedTable => {
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];

    const jsonData = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1, raw: true });
    const filteredData = jsonData.filter(row =>
        row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== '')
    );

    if (filteredData.length === 0) return { headers: [], rows: [] };

    const firstRow = filteredData[0].map(cell => String(cell || ''));

    const headerMatchCount = firstRow.filter(cell => {
        const normalized = String(cell).toLowerCase().trim();
        if (!normalized) return false;
        return Object.values(FIELD_DEFINITIONS).some(def =>
            def.keywords.some(k => {
                const keyword = k.toLowerCase();
                return normalized === keyword ||
                       normalized === keyword + ':' ||
                       normalized.split(/\s+/).includes(keyword);
            })
        );
    }).length;

    const hasHeaders = headerMatchCount >= 2;

    const convertCell = (cell: unknown): string => {
        if (cell === undefined || cell === null) return '';
        return String(cell);
    };

    if (hasHeaders) {
        return {
            headers: firstRow,
            rows: filteredData.slice(1).map(row => row.map(convertCell))
        };
    }

    return {
        headers: [],
        rows: filteredData.map(row => row.map(convertCell))
    };
};

export interface ColumnMapping {
    index: number;
    originalHeader: string;
    detectedField: DetectedField;
}

export interface ParseFileResult {
    rawRows: string[][];
    columnMappings: ColumnMapping[];
    totalRows: number;
}

// Main parse function
export const parseFile = async (file: File): Promise<ParseFileResult> => {
    const fileName = file.name.toLowerCase();
    let parsed: ParsedTable;

    if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) {
        const content = await file.text();
        parsed = parseCSV(content);
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        const buffer = await file.arrayBuffer();
        parsed = parseExcel(buffer);
    } else {
        throw new Error('סוג קובץ לא נתמך. נא להעלות קובץ CSV או Excel.');
    }

    if (parsed.rows.length === 0) {
        throw new Error('הקובץ ריק או לא ניתן לקרוא אותו.');
    }

    // Determine max columns
    const maxColumns = Math.max(
        parsed.headers.length,
        ...parsed.rows.map(row => row.length)
    );

    // Normalize rows to have same number of columns
    const normalizedRows = parsed.rows.map(row => {
        const normalized = [...row];
        while (normalized.length < maxColumns) {
            normalized.push('');
        }
        return normalized;
    });

    // Normalize headers to have same number of columns
    const normalizedHeaders = [...parsed.headers];
    while (normalizedHeaders.length < maxColumns) {
        normalizedHeaders.push('');
    }

    // Generate column mappings
    const columnMappings: ColumnMapping[] = [];
    for (let i = 0; i < maxColumns; i++) {
        let detectedField: DetectedField = 'unknown';
        const headerValue = normalizedHeaders[i]?.trim() || '';

        // Try to detect from header first
        if (headerValue) {
            detectedField = detectFieldFromHeader(headerValue);
        }

        // If not detected from header, try from data patterns
        if (detectedField === 'unknown') {
            const columnValues = normalizedRows.map(row => row[i]);
            const dataDetected = detectFieldFromData(columnValues);
            if (dataDetected !== 'unknown') {
                detectedField = dataDetected;
            }
        }

        columnMappings.push({
            index: i,
            originalHeader: headerValue || `עמודה ${i + 1}`,
            detectedField
        });
    }

    return {
        rawRows: normalizedRows,
        columnMappings,
        totalRows: normalizedRows.length
    };
};

export interface ParsedExercise {
    rowIndex: number;
    workoutId: string;
    exerciseName: string;
    sets: number | null;
    reps: number | null;
    restTime: number;
    maxReps: number | null;
    duration: number | null;
    tempo: string | null;
    typeSet: string | null;
    manipulation: string | null;
    comment: string;
}

// Transform raw data to exercise rows grouped by workout
export const transformToExercises = (
    rawRows: string[][],
    columnMappings: ColumnMapping[]
): ParsedExercise[] => {
    const exercises: ParsedExercise[] = [];

    rawRows.forEach((row, rowIndex) => {
        const exercise: ParsedExercise = {
            rowIndex,
            workoutId: '',
            exerciseName: '',
            sets: null,
            reps: null,
            restTime: DEFAULT_REST_TIME_SECONDS,
            maxReps: null,
            duration: null,
            tempo: null,
            typeSet: null,
            manipulation: null,
            comment: ''
        };

        columnMappings.forEach(mapping => {
            if (mapping.detectedField && mapping.detectedField !== 'ignore' && mapping.detectedField !== 'unknown') {
                const value = row[mapping.index] || '';

                switch (mapping.detectedField) {
                    case 'workoutId':
                        exercise.workoutId = value;
                        break;
                    case 'exerciseName':
                        exercise.exerciseName = value;
                        break;
                    case 'sets':
                        exercise.sets = parseInt(value) || null;
                        break;
                    case 'reps':
                        exercise.reps = parseInt(value) || null;
                        break;
                    case 'restTime': {
                        // Rest time is required downstream, and `parseInt(value) || null` mapped a
                        // legitimate "0" (cardio rests aren't timed) to null — which persisted as
                        // rest_time = NULL and hung the mobile timer on "NaN:NaN". Keep 0, and fall
                        // back to the default only when the cell is genuinely blank/non-numeric.
                        const parsedRestTime = parseInt(value);
                        exercise.restTime = Number.isNaN(parsedRestTime)
                            ? DEFAULT_REST_TIME_SECONDS
                            : parsedRestTime;
                        break;
                    }
                    case 'maxReps':
                        exercise.maxReps = parseInt(value) || null;
                        break;
                    case 'duration':
                        exercise.duration = parseInt(value) || null;
                        break;
                    case 'tempo':
                        const tempoVal = value.toUpperCase().trim();
                        exercise.tempo = VALID_TEMPO_VALUES.includes(tempoVal) ? tempoVal : null;
                        break;
                    case 'typeSet':
                        const typeSetVal = value.toUpperCase().trim().replace(/ /g, '_');
                        exercise.typeSet = VALID_TYPE_SET_VALUES.includes(typeSetVal) ? typeSetVal : null;
                        break;
                    case 'manipulation':
                        const manipVal = value.toUpperCase().trim();
                        exercise.manipulation = VALID_MANIPULATION_VALUES.includes(manipVal) ? manipVal : null;
                        break;
                    case 'comment':
                        exercise.comment = value;
                        break;
                    default:
                        break;
                }
            }
        });

        // Only add if we have at least an exercise name
        if (exercise.exerciseName.trim()) {
            exercises.push(exercise);
        }
    });

    return exercises;
};

export interface GroupedWorkout {
    identifier: string;
    name: string;
    nameHeb: string;
    exercises: ParsedExercise[];
}

// Group exercises by workout ID
export const groupExercisesByWorkout = (exercises: ParsedExercise[]): GroupedWorkout[] => {
    const workoutMap = new Map<string, GroupedWorkout>();

    exercises.forEach(exercise => {
        const workoutId = exercise.workoutId || 'A'; // Default to 'A' if no workout ID

        if (!workoutMap.has(workoutId)) {
            workoutMap.set(workoutId, {
                identifier: workoutId,
                name: `Workout ${workoutId}`,
                nameHeb: `אימון ${workoutId}`,
                exercises: []
            });
        }

        workoutMap.get(workoutId)!.exercises.push(exercise);
    });

    // Sort workouts by identifier
    const sortedWorkouts = Array.from(workoutMap.values()).sort((a, b) =>
        a.identifier.localeCompare(b.identifier)
    );

    return sortedWorkouts;
};

// Extract unique exercise names for matching
export const extractUniqueExerciseNames = (exercises: ParsedExercise[]): string[] => {
    const uniqueNames = new Set<string>();
    exercises.forEach(exercise => {
        if (exercise.exerciseName.trim()) {
            uniqueNames.add(exercise.exerciseName.trim());
        }
    });
    return Array.from(uniqueNames);
};
